import { faArrowsRotate } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { EpaForm, FileTypeEnum } from "../../../apiClient/generated";
import { ALLOWED_FILE_EXTENSIONS } from "../../../constants/globals";
import { useEpaSepApiClient } from "../../../hooks";
import { useFileUploader } from "../../FileUploadField/hooks";
import { FileItemDisplay } from "../../FileUploadField/ui";
import { LoadingIndicator } from "../../MapV2/ui/LoadingIndicator";
import UploadDropdown from "../../UploadDropdown";

const useEpaFileUploader = (
    form: EpaForm,
    uploadType: FileTypeEnum,
    uploadCompleteCallback?: () => void,
) => {
    // API client
    const apiClient = useEpaSepApiClient();

    const createUpload = useCallback(
        async (file: string) => {
            return await apiClient.epaSepFormFilesCreate({
                id: form.id,
                epaFormFileCreateRequest: {
                    file,
                    fileType: uploadType,
                },
            });
        },
        [apiClient, form, uploadType],
    );

    const markAsComplete = useCallback(
        async (fileId: string) => {
            await apiClient.epaSepFormFilesUpdate({
                id: form.id,
                epaFormFileOperationRequest: { fileId },
            });
        },
        [apiClient, form],
    );

    const deleteUpload = useCallback(
        async (fileId: string) => {
            return await apiClient.epaSepFormFilesDestroy({
                id: form.id,
                fileId,
            });
        },
        [apiClient, form],
    );

    return useFileUploader(
        createUpload,
        markAsComplete,
        deleteUpload,
        uploadCompleteCallback,
    );
};

interface EpaFormUploadField {
    form: EpaForm;
    uploadType: FileTypeEnum;
}

export const EpaFormUploadField = (props: EpaFormUploadField) => {
    const queryClient = useQueryClient();
    const apiClient = useEpaSepApiClient();

    // Retrieve files
    const filesQuery = useQuery({
        queryKey: ["epaFormFiles", props.form.id],
        queryFn: async () => {
            return await apiClient.epaSepFormFilesList({
                id: props.form.id,
            });
        },
        refetchOnWindowFocus: false,
        refetchInterval: (d) => {
            if (d && d.some((f) => !f.scanComplete)) {
                // While there are files not scanned, refresh every 30s.
                return 30000;
            }
            return false;
        },
    });
    const uploadedFilenames = useMemo(() => {
        return filesQuery.data?.map((i) => i.file);
    }, [filesQuery.data]);

    const dataUploader = useEpaFileUploader(props.form, props.uploadType, () =>
        queryClient.invalidateQueries({
            queryKey: ["epaFormFiles", props.form.id],
        }),
    );

    // When files are added to dropzone
    const onDropFiles = (files: FileList) => {
        if (!files) return;
        const newFiles = [...files];
        const filesToUpload = newFiles.filter(
            (f) => !uploadedFilenames.includes(f.name),
        );
        dataUploader.addFiles(filesToUpload);
    };

    const deleteFileMutation = useMutation({
        mutationKey: ["epaFormFiles", props.form.id],
        mutationFn: (fileId: string) => {
            return apiClient.epaSepFormFilesDestroy({
                id: props.form.id,
                fileId,
            });
        },
        onSuccess: () => {
            // Invalidate and refetch the files query
            queryClient.invalidateQueries({
                queryKey: ["epaFormFiles", props.form.id],
            });
        },
    });

    return (
        <div className="flex flex-col gap-2">
            <UploadDropdown
                accept={ALLOWED_FILE_EXTENSIONS.join(",")}
                onChange={onDropFiles}
                multiple
            />
            {Object.values(dataUploader.uploadState)
                .filter((u) => u.status !== "success")
                .map((upload) => (
                    <FileItemDisplay
                        key={`${upload.file?.name}_${upload.fileId}`}
                        filename={upload.file?.name}
                        extraInfo={
                            upload.status === "uploading" &&
                            upload.loaded &&
                            upload.total ? (
                                `${((upload.loaded / upload.total) * 100).toFixed(1)} %`
                            ) : upload.status === "uploading" &&
                              !upload.loaded &&
                              !upload.total ? (
                                <FontAwesomeIcon
                                    icon={faArrowsRotate}
                                    className="w-4 animate-spin"
                                />
                            ) : undefined
                        }
                        error={upload.error}
                        scanComplete={false}
                        virusDetected={false}
                        onDelete={() =>
                            dataUploader.removeFile(upload.file?.name)
                        }
                    />
                ))}
            {filesQuery.isLoading && <LoadingIndicator />}
            {filesQuery.data
                ?.filter((f) => f.fileType === props.uploadType)
                .map((file) => (
                    <FileItemDisplay
                        key={file.id}
                        filename={file.filename}
                        extraInfo={
                            !file.scanComplete
                                ? "Waiting AV check..."
                                : undefined
                        }
                        downloadUrl={file.file}
                        scanComplete={file.scanComplete}
                        virusDetected={file.virusDetected}
                        onDelete={() => deleteFileMutation.mutate(file.id)}
                    />
                ))}
        </div>
    );
};
